<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import { draftHasItems } from '@/domain/menu-import/menu-draft';
  import {
    readAgainOffered,
    sidePanelUseful,
    workspacePhase,
  } from '@/domain/menu-import/workspace-view';

  /**
   * The workspace's part of the top bar (plan §4.5 «Top bar actions»): the
   * save indicator («Guardando…», «Guardado», offline, or «Reintentar» once
   * the retries ran out) and «Deshacer»; on narrow screens a ⋯ menu holds
   * «Deshacer», «Carta original» (not with the AI off and no menu photo),
   * «Cargar otra vez» (not with the AI off once the menu has dishes) and
   * «Seguir después». The failed read has nothing of the owner to save or
   * undo: no save indicator (unless saves wait for «Reintentar») and no
   * «Deshacer» there.
   * Rendered by the page inside `V2MenuContextBridge` (it lives outside the
   * menu flow); shows nothing outside the workspace.
   */
  defineProps<{ wide: boolean }>();
  const emit = defineEmits<{ (e: 'later'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ctx = useMenuImport();
  const online = useOnline();

  const shown = computed(
    () => ctx.loadState.value === 'ready' && ctx.view.value === 'workspace'
  );
  const phase = computed(() =>
    workspacePhase(ctx.read.value, ctx.draft.value, ctx.manual.value)
  );
  const reading = computed(() => phase.value === 'reading');
  /** The latest read failed and nothing was built (review round 5, I-3). */
  const failed = computed(() => phase.value === 'failed');
  /** With the AI off: «Carta original» only with menu photos (review round 4, m4). */
  const aiEnabled = computed(() => ctx.aiEnabled.value);
  const hasOriginal = computed(() =>
    sidePanelUseful(aiEnabled.value, ctx.pages.value)
  );
  /** With the AI off, «Cargar otra vez» only while the menu is empty (review round 5, m2). */
  const hasReadAgain = computed(() =>
    readAgainOffered(aiEnabled.value, draftHasItems(ctx.draft.value))
  );
  const undoDisabled = computed(
    () => !ctx.canUndo.value || reading.value || !online.value
  );
  const undoTitle = computed(() =>
    reading.value ? tr('menuImport.bar.readingDisabled') : undefined
  );
  const saveState = computed(() => ctx.saveState.value);
  const saveLabel = computed(() => {
    switch (saveState.value) {
      case 'saving':
        return tr('storeSetup.saving');
      case 'offline':
        return tr('storeSetup.savingOffline');
      default:
        return tr('storeSetup.saved');
    }
  });
  const saveIcon = computed(() =>
    saveState.value === 'offline' ? 'wifi-off' : 'check'
  );

  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  function undo() {
    if (undoDisabled.value) return;
    void ctx.undo();
  }

  function openOriginal() {
    ctx.sidePanelTab.value = 'original';
    ctx.sheetOpen.value = true;
  }

  /** «Cargar otra vez»: the source screen; the next read joins this menu. */
  function readAgain() {
    blurActive();
    ctx.view.value = 'source';
  }
</script>

<template>
  <div v-if="shown" class="v2-menu-top-actions">
    <button
      v-if="saveState === 'failed'"
      type="button"
      class="save-retry"
      @click="ctx.retrySaves()"
    >
      <PosUiIcon name="alert-circle-outline" class="save-icon" />
      <span>{{ $t('menuImport.tray.retry') }}</span>
    </button>
    <p v-else v-show="!failed" class="save-state" role="status">
      <span
        v-if="saveState === 'saving'"
        class="save-spinner"
        aria-hidden="true"
      />
      <PosUiIcon v-else :name="saveIcon" class="save-icon" />
      <span class="save-label">{{ saveLabel }}</span>
    </p>
    <PosUiButton
      v-if="wide"
      v-show="!failed"
      color="neutral"
      variant="ghost"
      icon="undo"
      :disabled="undoDisabled"
      :title="undoTitle"
      @click="undo"
      >{{ $t('menuImport.undo') }}</PosUiButton
    >
    <PosUiPopover v-else placement="bottom" align="end">
      <template #trigger="{ open, toggle }">
        <button
          type="button"
          class="more-trigger"
          :aria-label="$t('menuImport.more')"
          :aria-expanded="open ? 'true' : 'false'"
          aria-haspopup="true"
          @click="toggle"
        >
          <PosUiIcon name="dots-horizontal" :size="22" />
        </button>
      </template>
      <template #default="{ close }">
        <div class="more-items">
          <PosUiButton
            v-if="!failed"
            color="neutral"
            variant="ghost"
            icon="undo"
            block
            class="more-item"
            :disabled="undoDisabled"
            @click="
              close();
              undo();
            "
            >{{ $t('menuImport.undo') }}</PosUiButton
          >
          <PosUiButton
            v-if="hasOriginal"
            color="neutral"
            variant="ghost"
            icon="image-outline"
            block
            class="more-item"
            :disabled="phase !== 'review'"
            @click="
              close();
              openOriginal();
            "
            >{{ $t('menuImport.original') }}</PosUiButton
          >
          <PosUiButton
            v-if="hasReadAgain"
            color="neutral"
            variant="ghost"
            icon="camera-plus-outline"
            block
            class="more-item"
            :disabled="reading"
            @click="
              close();
              readAgain();
            "
            >{{ $t('menuImport.bar.readAgain') }}</PosUiButton
          >
          <PosUiButton
            color="neutral"
            variant="ghost"
            icon="clock-outline"
            block
            class="more-item"
            @click="
              close();
              emit('later');
            "
            >{{ $t('menuImport.bar.later') }}</PosUiButton
          >
        </div>
      </template>
    </PosUiPopover>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-top-actions {
    @apply tw-flex tw-items-center tw-gap-1;
    .save-state {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-px-1 tw-text-xs tw-text-neutral-500;
    }
    .save-icon {
      @apply tw-text-base;
    }
    // Below xl only the icon shows (the title keeps its room in the top bar);
    // the words stay for screen readers.
    .save-label {
      @apply tw-sr-only xl:tw-not-sr-only;
    }
    .save-spinner {
      @apply tw-h-3.5 tw-w-3.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-300;
      border-top-color: theme('colors.neutral.600');
      animation: save-spin 0.8s linear infinite;
    }
    .save-retry {
      @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-text-sm tw-font-medium tw-text-red-700 hover:tw-bg-red-50;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .more-trigger {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-700 hover:tw-bg-neutral-100;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .more-items {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .more-item {
      @apply tw-justify-start;
    }
  }

  @keyframes save-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-top-actions .save-spinner {
      animation: none;
    }
  }
</style>
